import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import type { OwnerCommandDto, OwnerCommandStatus } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';

const AUTO_REFRESH_MS = 60_000;

const STATUS_BADGE: Record<OwnerCommandStatus, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  pending: { label: 'Waiting for the server', variant: 'warning' },
  applied: { label: 'Applied', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
  expired: { label: 'Expired', variant: 'secondary' },
};

export function TenantCommandsCard({ tenantId }: { tenantId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const key = ['tenant-commands', tenantId];

  const commands = useQuery({
    queryKey: key,
    queryFn: () => get<OwnerCommandDto[]>(`/owner/tenants/${tenantId}/commands`),
    refetchInterval: (query) => ((query.state.data ?? []).some((command) => command.status === 'pending') ? visibleInterval(AUTO_REFRESH_MS)() : false),
  });

  const cancel = useMutation({
    mutationFn: (commandId: string) => post<OwnerCommandDto>(`/owner/tenants/${tenantId}/commands/${commandId}/cancel`, { totpCode }),
    onSuccess: () => {
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success('Cancelled');
    },
    onError: (error) => toast.error('Could not cancel', describeError(error)),
  });

  const syncNow = useMutation({
    mutationFn: () => post(`/owner/tenants/${tenantId}/sync-now`, {}),
    onSuccess: () => toast.success('Sync requested', 'The install picks it up at its next check-in'),
    onError: (error) => toast.error('Could not request a sync', describeError(error)),
  });

  const requeue = useMutation({
    mutationFn: () => post(`/owner/tenants/${tenantId}/sync/requeue-parked`, { totpCode }),
    onSuccess: () => {
      setTotpCode('');
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success('Queued', 'The facility server sends its set-aside changes again at its next check-in');
    },
    onError: (error) => toast.error('Could not queue it', describeError(error)),
  });

  const rows = commands.data ?? [];
  const cancellable = rows.some((row) => row.status === 'pending' && !row.deliveredAt);

  return (
    <Card className="lg:col-span-3">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">Owner actions sent to this facility&apos;s own server</CardTitle>
        <Button variant="outline" size="sm" className="gap-1.5" loading={syncNow.isPending} onClick={() => syncNow.mutate()}>
          <Send className="h-3.5 w-3.5" /> Sync now
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Account actions for a facility that runs on its own server are queued here and applied by that server at its next check-in, because its own
          server is where the people are managed. Until then the facility keeps working as before.
        </p>
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing has been sent.</p>}
        {rows.length > 0 && (
          <ul className="divide-y rounded-md border">
            {rows.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                <Badge variant={STATUS_BADGE[row.status].variant}>{STATUS_BADGE[row.status].label}</Badge>
                <span className="flex-1">{row.summary}</span>
                <span className="text-xs text-muted-foreground">
                  {row.completedAt ? `${row.resultMessage ?? ''} · ${dateTime(row.completedAt)}` : `Sent ${dateTime(row.issuedAt)}`}
                </span>
                {row.status === 'pending' && !row.deliveredAt && (
                  <Button variant="outline" size="sm" disabled={!isValidTotp(totpCode) || cancel.isPending} onClick={() => cancel.mutate(row.id)}>
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <TotpField value={totpCode} onChange={setTotpCode} />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" disabled={!isValidTotp(totpCode) || requeue.isPending} onClick={() => requeue.mutate()}>
            Send set-aside changes again
          </Button>
          <span className="text-xs text-muted-foreground">
            {cancellable ? 'The code above also cancels an action that has not been delivered yet.' : 'Uses the code above.'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
